import 'react-app-polyfill/ie11'
import 'core-js/stable'
import 'string.prototype.startswith'
import find from 'array.prototype.find'

import * as Sentry from '@sentry/browser'
import React from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter as Router } from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import { ApolloProvider } from 'react-apollo'
import createApolloClient from '@dooer/apollo-multipart-network-interface'
import { ThemeProvider } from '@dooer/react-ui-kit'

import App from './app'
import * as serviceWorker from './serviceWorker'
import { getApiBaseUrl, getEnv, storageAvailable } from './lib/utils'
import sv from './messages/sv.json'

import introspectionQueryResultData from './fragmentTypes.json'
import uiKitTheme from './css/ui-kit-theme'

find.shim()

if (getEnv() === 'dooer') {
  Sentry.init({
    dsn: 'https://d4bfac70f84343ea8588854f43616b25@sentry.io/1532667',
  })
}

// Global token
let token: string | null = null

// Token functions
export const setTokenAndOrgId = (newToken: string, newOrgId: string) => {
  token = newToken
  if (storageAvailable('localStorage')) {
    window.localStorage.setItem('token', newToken)
    window.localStorage.setItem('organizationId', newOrgId)
  }
}
export const getToken = () => token
export const removeTokenAndOrgId = () => {
  token = null
  if (storageAvailable('localStorage')) {
    window.localStorage.removeItem('token')
    window.localStorage.removeItem('organizationId')
  }
}

// If localStorage is available try using token from there instaed
if (storageAvailable('localStorage')) {
  token = window.localStorage.getItem('token')
}

const client = createApolloClient({
  uri: `${getApiBaseUrl()}/graphql`,
  clientName: 'frontend-website',
  getToken: () => token,
  getLanguageCode: () => 'sv',
  introspectionQueryResultData,
  errorHandler(input) {
    const { graphQLErrors = [], operation } = input
    // Custom error handlers can be set in query definition or (in theory) by an apollo link
    const customErrorHandler =
      (operation.getContext() && operation.getContext().errorHandler) || operation.variables.errorHandler
    if (customErrorHandler) {
      return customErrorHandler(input)
    }
    for (const error of graphQLErrors) {
      if (['unauthorized', 'invalid-token'].includes(error.extensions && error.extensions.code)) {
        removeTokenAndOrgId()
      } else {
        console.error(error) // eslint-disable-line no-console
      }
    }
  },
})

function AppProviders(props: { children: React.ReactNode }) {
  return (
    <Router>
      <IntlProvider locale="sv" messages={sv}>
        <ApolloProvider client={client}>
          <ThemeProvider theme={uiKitTheme}>{props.children}</ThemeProvider>
        </ApolloProvider>
      </IntlProvider>
    </Router>
  )
}

function run() {
  const root = window.document.getElementById('root')

  if (root && root.hasChildNodes()) {
    ReactDOM.hydrate(
      <AppProviders>
        <App />
      </AppProviders>,
      root
    )
  } else {
    ReactDOM.render(
      <AppProviders>
        <App />
      </AppProviders>,
      root
    )
  }
}

if (!global.Intl) {
  import('intl').then(() => import('intl/locale-data/jsonp/sv')).then(() => run())
} else {
  run()
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
